import Label from 'components/Label/Label';
import React, { FC, useEffect, useState } from 'react';
import ButtonPrimary from 'shared/Button/ButtonPrimary';
import Input from 'shared/Input/Input';
// import Select from 'shared/Select/Select';
// import Textarea from 'shared/Textarea/Textarea';
import CommonLayout from './CommonLayout';
import { Helmet } from 'react-helmet-async';
// import { avatarImgs } from 'contains/fakeData';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { FormFeedback } from 'reactstrap';
import { AppDispatch, RootState } from 'reduxStore/store';
import axios from 'service/axios';
import { toast } from 'react-toastify';
import { AlertCircle, CheckCircle } from 'react-feather';
import { verifyToken } from 'reduxStore/store/auth';
import { fetchOtpChannels } from 'service/authApi';
import MobileVerifyModal, { verifiableAccountMobile, isIndianDialCode } from 'components/MobileVerify/MobileVerifyModal';

/**
 * The stored country code is the bare calling code ("91", "1"); people read it
 * with a plus. Nothing stored shows nothing.
 */
const displayDialCode = (code?: string | null): string => {
    const digits = String(code ?? '').replace(/\D/g, '');
    return digits ? `+${digits}` : '';
};

/** The green "Verified" pill, the same claim the app shows beside a verified email / mobile. */
const VerifiedBadge: FC<{ testId: string }> = ({ testId }) => (
    <span
        className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400"
        data-testid={testId}
    >
        <CheckCircle size={12} aria-hidden="true" />
        Verified
    </span>
);

export interface AccountPageProps {
    className?: string;
}

const AccountPage: FC<AccountPageProps> = ({ className = '' }) => {
    const dispatch = useDispatch<AppDispatch>();

    const authData = useSelector((state: RootState) => state?.auth);
    const [buttonStatus, setButtonStatus] = useState<boolean>(false);

    // WhatsApp sign-in status. Offered only while the server has WhatsApp on.
    const [whatsappAvailable, setWhatsappAvailable] = useState(false);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const mobileVerified = Boolean(authData?.userData?.mobileVerified);
    const emailVerified = Boolean(authData?.userData?.emailVerified);
    const verifiableMobile = verifiableAccountMobile(authData?.userData?.customer);

    useEffect(() => {
        let cancelled = false;
        fetchOtpChannels().then((channels) => {
            if (!cancelled) setWhatsappAvailable(channels.includes('whatsapp'));
        });
        return () => {
            cancelled = true;
        };
    }, []);

    // validation Object
    let validationObj = {
        firstName: yup.string().required('First Name is required.'),
        lastName: yup.string().required('Last Name is required.'),
        email: yup.string().required('Email is required.').email('Must be a valid email.'),
        mobile: yup
            .number()
            .required('Mobile is required.')
            .typeError('Mobile must be a number.')
            .test('len', 'Number must be at least 10 digits', (val) => val.toString().length >= 10)
            .nullable()
    };
    const validationScheme = yup.object().shape(validationObj);

    // Hook Form
    const {
        register,
        reset,
        handleSubmit,

        formState: { errors }
    } = useForm({
        mode: 'onChange',
        resolver: yupResolver(validationScheme)
    });
    const createProfile = async (data: any) => {
        return await axios.put('/customer', data, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
    };

    const onSubmit = async (formData: any) => {
        setButtonStatus(true);

        createProfile(formData)
            .then((res) => {
                setButtonStatus(false);

                toast.success('Account updated successfully.');
                dispatch(verifyToken());
            })
            .catch((err) => {
                setButtonStatus(false);

                toast.error(err?.response?.data?.message || 'Something went wrong!');
            });
    };

    useEffect(() => {
        reset({
            firstName: authData?.userData?.customer?.firstName || '',
            lastName: authData?.userData?.customer?.lastName || '',
            email: authData?.userData?.email || '',
            mobile: authData?.userData?.customer?.mobile || ''
        });
    }, [authData]);

    return (
        <div className={`nc-AccountPage ${className}`} data-nc-id="AccountPage">
            <Helmet>
                <title>Account | Instaacard ecommerce React Template</title>
            </Helmet>
            <CommonLayout>
                <div className="space-y-10 sm:space-y-12">
                    {/* HEADING */}
                    <h2 className="text-2xl sm:text-3xl font-semibold">Account infomation</h2>
                    <div className="flex flex-col md:flex-row">
                        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                            <div className="flex-grow mt-10 md:mt-0  max-w-3xl space-y-6">
                                <div>
                                    <Label>First Name</Label>
                                    <Input className="mt-1.5" {...register('firstName')} />
                                    {errors && errors.firstName && <FormFeedback>{errors.firstName.message}</FormFeedback>}
                                </div>
                                <div>
                                    <Label>Last Name</Label>
                                    <Input className="mt-1.5" {...register('lastName')} />
                                    {errors && errors.lastName && <FormFeedback>{errors.lastName.message}</FormFeedback>}
                                </div>

                                <div>
                                    <div className="flex items-center gap-2">
                                        <Label>Email</Label>
                                        {emailVerified && <VerifiedBadge testId="account-email-verified-badge" />}
                                    </div>
                                    <div className="mt-1.5 flex">
                                        <span className="inline-flex items-center px-2.5 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                                            <i className="text-2xl las la-envelope"></i>
                                        </span>
                                        <Input
                                            className="!rounded-l-none bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 cursor-not-allowed focus:border-neutral-200 focus:ring-0"
                                            placeholder="example@email.com"
                                            defaultValue={authData?.userData?.email}
                                            readOnly
                                        />
                                    </div>
                                    <p className="mt-1.5 text-xs text-neutral-400">
                                        Your email can&apos;t be changed here. Contact support to update it.
                                    </p>
                                </div>

                                {/* ---- */}
                                <div>
                                    <div className="flex items-center gap-2">
                                        <Label>Phone number</Label>
                                        {mobileVerified && <VerifiedBadge testId="account-mobile-verified-badge" />}
                                    </div>
                                    <div className="mt-1.5 flex">
                                        <span className="inline-flex items-center px-2.5 rounded-l-2xl border border-r-0 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 text-sm">
                                            <i className="text-2xl las la-phone-volume"></i>
                                        </span>
                                        <Input
                                            className="!rounded-l-none bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 cursor-not-allowed focus:border-neutral-200 focus:ring-0"
                                            // The code is stored beside the number, without a
                                            // plus; an older account without one shows the
                                            // number alone.
                                            value={[
                                                displayDialCode(authData?.userData?.customer?.countryCode),
                                                authData?.userData?.customer?.mobile
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            readOnly
                                        />
                                    </div>
                                    <p className="mt-1.5 text-xs text-neutral-400">
                                        Your phone number can&apos;t be changed here. Contact support to update it.
                                    </p>

                                    {/* WhatsApp sign-in. Verifies the number already on the
                                        account -- changing it stays a support request. */}
                                    {mobileVerified && (
                                        <p
                                            className="mt-3 flex items-center gap-2 text-sm text-green-600"
                                            data-testid="account-mobile-verified"
                                        >
                                            <CheckCircle size={16} aria-hidden="true" />
                                            Verified on WhatsApp — you can sign in with this number.
                                        </p>
                                    )}

                                    {/* Not verified yet: why it matters, and the same pop-up as
                                        the first sign-in. */}
                                    {/* Not gated on a number being on file: a cleared account
                                        has none, and that is exactly when this needs to show.
                                        Still hidden for a non-Indian dial code, which WhatsApp
                                        cannot verify. */}
                                    {!mobileVerified &&
                                        whatsappAvailable &&
                                        isIndianDialCode(authData?.userData?.customer?.countryCode) && (
                                            <div
                                                className="mt-3 flex flex-wrap items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-900/20"
                                                data-testid="account-mobile-verify-note"
                                            >
                                                <AlertCircle size={18} className="mt-0.5 flex-shrink-0 text-amber-600" aria-hidden="true" />
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-medium text-neutral-900 dark:text-neutral-100">
                                                        {verifiableMobile ? 'Mobile number not verified' : 'Mobile number not added'}
                                                    </p>
                                                    <p className="mt-0.5 text-neutral-600 dark:text-neutral-400">
                                                        {verifiableMobile
                                                            ? `Verify +91 ${verifiableMobile} on WhatsApp to sign in with a code sent there.`
                                                            : 'Add your mobile number to sign in with a code sent on WhatsApp.'}
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="self-center font-medium text-green-600 hover:underline"
                                                    onClick={() => setVerifyOpen(true)}
                                                    data-testid="account-mobile-verify-open"
                                                >
                                                    Verify on WhatsApp
                                                </button>
                                            </div>
                                        )}

                                    {verifyOpen && (
                                        <MobileVerifyModal
                                            open={verifyOpen}
                                            mobile={verifiableMobile}
                                            accessToken={authData?.accessToken || undefined}
                                            onClose={() => setVerifyOpen(false)}
                                        />
                                    )}
                                </div>
                                {/* ---- */}

                                <div className="pt-2">
                                    <ButtonPrimary disabled={buttonStatus} type="submit">
                                        Update account
                                    </ButtonPrimary>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </CommonLayout>
        </div>
    );
};

export default AccountPage;
